import { useCallback, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import { StatusCard } from "../../components/StatusCard";
import { SubStepShell } from "../../components/SubStepShell";
import { TallyOpeningBalanceGrid } from "../tally-opening/TallyOpeningBalanceGrid";
import { TallyOpeningImportPanel } from "../tally-opening/TallyOpeningImportPanel";
import { useSubStepNav } from "../useSubStepNav";

function formatStartDate(value: string): string {
  const at = new Date(`${value}T00:00:00`);
  return Number.isNaN(at.getTime())
    ? value
    : at.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

export function TallyOpeningBalancesStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "opening-balances");
  const [revision, setRevision] = useState(0);
  const [gridBalanced, setGridBalanced] = useState(true);
  const onChanged = useCallback(() => setRevision((value) => value + 1), []);

  return (
    <SubStepShell
      title="Set Up Opening Balances"
      description="Opening balances will be set up as of the Account Start Date shown below."
      thenWhat={
        gridBalanced
          ? "Continue → Setting up Tally."
          : "Debits and credits must agree in the ledger balances to continue."
      }
      onContinue={() => void goNext()}
      continueDisabled={!gridBalanced}
    >
      <div className="flex flex-col gap-1 rounded-lg border-2 border-primary/30 bg-primary/5 px-5 py-4">
        <span className="text-xs font-medium uppercase tracking-wide text-primary">Account Start Date</span>
        <span className="text-2xl font-semibold tabular-nums">
          {client?.accountStartDate ? formatStartDate(client.accountStartDate) : "Not set yet"}
        </span>
      </div>

      <StatusCard
        title="Ledger balances"
        description="Enter the opening balance for each ledger as of the Account Start Date."
        className="min-[1366px]:-mx-6 min-[1440px]:-mx-12"
      >
        <TallyOpeningBalanceGrid
          clientId={clientId}
          reloadToken={revision}
          onBalancedChange={setGridBalanced}
          beforeGroups={(locked) => (
            <TallyOpeningImportPanel clientId={clientId} disabled={locked} onApplied={onChanged} />
          )}
        />
      </StatusCard>
    </SubStepShell>
  );
}
